import { useState } from "react";
import { Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { ArrowUpRight, BadgeCheck, Loader2, X } from "lucide-react";
import { useLang } from "@/lib/i18n";
import VerifiedBadge from "@/components/yts/business/VerifiedBadge";
import { readAffiliateAttribution } from "@/lib/affiliateAttribution";
import { isPremium } from "@/data/plans";

const inputCls =
  "w-full border border-[#171717] bg-white px-3 py-2.5 text-[14px] outline-none focus:bg-[#f7f7f4] transition-colors";
const labelCls = "block font-mono text-[10px] tracking-[0.2em] uppercase font-bold text-[#6d6d6a] mb-1.5";
const ROLES = ["Owner", "Manager", "Family member", "Agency", "Other"];

const emptyClaim = { claimant_name: "", claimant_email: "", claimant_phone: "", role: "Owner", website: "", description: "" };

/**
 * The owner-facing claim module on a business page, plus the short form behind
 * it. Everything is written as a BusinessClaim the site team reviews by hand.
 */
export default function ClaimBusinessButton({ listing, me }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [mine, setMine] = useState(null);
  const [checked, setChecked] = useState(false);
  const [form, setForm] = useState(emptyClaim);
  const [promoCode, setPromoCode] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const owned = !!listing.owner_user_id;
  const isMine = owned && !!me && listing.owner_user_id === me.id;
  const premiumPresentation = /loop experience/i.test(listing.name || "") || isPremium(listing);

  const openForm = async () => {
    setOpen(true);
    const attribution = readAffiliateAttribution();
    if (attribution?.promo_code) setPromoCode((current) => current || attribution.promo_code);
    if (attribution?.referral_code) {
      base44.entities.AffiliateEvent.create({
        referral_code: attribution.referral_code,
        promo_code: promoCode || attribution.promo_code || "",
        event_type: "ClaimStarted", listing_id: listing.id,
        source_url: window.location.pathname, occurred_at: new Date().toISOString(),
      }).catch(() => {});
    }
    if (!me || checked) return;
    // RLS scopes this read to the visitor's own claims.
    try {
      const rows = await base44.entities.BusinessClaim.filter({ listing_id: listing.id });
      setMine(rows.find((c) => c.status === "Pending") || rows[0] || null);
    } catch {
      // no claim yet — the form is still the right next step
    } finally {
      setChecked(true);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.claimant_name.trim() || !form.claimant_email.trim() || !form.claimant_phone.trim()) {
      setErr(t("claim.required"));
      return;
    }
    setSending(true);
    setErr("");
    try {
      const attribution = readAffiliateAttribution();
      const submittedPromo = String(promoCode || attribution?.promo_code || "").trim().toUpperCase();
      const submittedReferral = String(attribution?.referral_code || "").trim().toUpperCase();
      let createdClaim = null;
      if (me?.id) {
        // Keep the existing owner/admin workflow for authenticated collaborators.
        createdClaim = await base44.entities.BusinessClaim.create({
          listing_id: listing.id,
          business_name: listing.name,
          ...form,
          affiliate_referral_code: submittedReferral,
          affiliate_promo_code: submittedPromo,
          affiliate_attributed_at: submittedReferral || submittedPromo ? new Date().toISOString() : "",
          status: "Pending",
          submitted_at: new Date().toISOString().slice(0, 10),
        });
      } else {
        // No public signup or access to the protected claim entity. Deliver
        // the guest request to the editors for manual review by email.
        const response = await base44.functions.invoke("contact-message", {
          name: form.claimant_name.trim(),
          email: form.claimant_email.trim(),
          message: [
            "Business ownership claim — no account required",
            `Business: ${listing.name}`,
            `Listing ID: ${listing.id}`,
            `Phone: ${form.claimant_phone}`,
            `Role: ${form.role}`,
            `Website/social proof: ${form.website}`,
            `Details: ${form.description}`,
            `Promo: ${submittedPromo}`,
            `Referral: ${submittedReferral}`,
          ].join("\n"),
        });
        if (!response?.data?.ok || !response?.data?.recipients) {
          throw new Error(response?.data?.error || "We couldn’t send your claim. Please try again.");
        }
      }
      // The claim is saved: count it as one marketing outcome.
      base44.analytics.track({
        eventName: "business_claim_submitted",
        properties: { listing_id: listing.id },
      });

      if (submittedReferral || submittedPromo) {
        base44.entities.AffiliateEvent.create({
          referral_code: submittedReferral || submittedPromo,
          promo_code: submittedPromo,
          event_type: "ClaimSubmitted", listing_id: listing.id,
          claim_id: createdClaim?.id || "", source_url: window.location.pathname,
          occurred_at: new Date().toISOString(),
        }).catch(() => {});
      }

      // Preserve a pending dashboard link for signed-in collaborators only.
      // Guest claims are delivered by email, not in a public account.
      // Preserve the exact incoming business so the dashboard can open
      // directly into this onboarding flow instead of showing every listing.
      try {
        if (me?.id) sessionStorage.setItem(
          "yts_pending_business_onboarding",
          JSON.stringify({
            listing_id: listing.id,
            claim_id: createdClaim?.id || null,
            created_at: Date.now(),
          })
        );
      } catch {
        // sessionStorage can be unavailable in privacy-restricted browsers.
      }

      setSent(true);
    } catch (e2) {
      setErr(e2.message || t("claim.error"));
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    setOpen(false);
    setErr("");
  };

  return (
    <>
      {/* One quiet line at the foot of the page. The claim itself is a single
          small pill — no plan, no price — so whoever owns the place finds it and
          nobody else has to read past it. */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        {owned ? (
          <p className="flex min-w-0 items-center gap-1.5 text-[12.5px] font-semibold leading-5 text-[#6d6d6a] dark:text-[#aaa9a1]">
            <BadgeCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="min-w-0">{isMine ? t("claim.mine") : t("claim.managed")}</span>
            {listing.verified && premiumPresentation && <VerifiedBadge size="sm" />}
          </p>
        ) : (
          <p className="min-w-0 text-[12.5px] font-medium leading-5 text-[#6d6d6a] dark:text-[#aaa9a1]">
            {t("claim.blurb")}
          </p>
        )}

        {isMine ? (
          <Link
            to="/business/dashboard"
            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-[#171717]/25 px-3.5 text-[12px] font-extrabold text-[#171717] transition-colors hover:border-[#171717] hover:bg-[#171717] hover:text-white dark:border-white/30 dark:text-[#f2efe9] dark:hover:bg-[#f2efe9] dark:hover:text-[#171614]"
          >
            {t("claim.dashboard")}
          </Link>
        ) : !owned ? (
          <button
            type="button"
            onClick={openForm}
            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-[#171717]/25 px-3.5 text-[12px] font-extrabold text-[#171717] transition-colors hover:border-[#171717] hover:bg-[#171717] hover:text-white dark:border-white/30 dark:text-[#f2efe9] dark:hover:bg-[#f2efe9] dark:hover:text-[#171614]"
          >
            {t("claim.cta")}
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[400] bg-black/60 backdrop-blur-[2px] flex items-end sm:items-center justify-center sm:p-6"
          onClick={close}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-[520px] max-h-[92vh] overflow-y-auto rounded-t-[22px] sm:rounded-[22px] shadow-[0_24px_60px_rgba(0,0,0,0.3)]"
          >
            <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-[#ededec] flex items-start justify-between gap-3 px-5 py-4">
              <div>
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase font-bold text-[#6d6d6a]">
                  {listing.name}
                </div>
                <h2 className="text-[20px] font-black tracking-[-0.03em] mt-0.5">{t("claim.title")}</h2>
              </div>
              <button
                onClick={close}
                aria-label="Close"
                className="w-9 h-9 shrink-0 grid place-items-center border border-[#171717] hover:bg-[#171717] hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-5">
              {sent ? (
                <div className="text-center py-4">
                  <BadgeCheck className="w-9 h-9 mx-auto text-[#171717]" />
                  <h3 className="text-[18px] font-black mt-2">{t("claim.sent")}</h3>
                  <p className="text-[14px] text-[#6d6d6a] mt-1.5 leading-[1.55]">{me ? t("claim.sentBlurb") : (t("claim.sentGuest") || "We’ll review your request and follow up by email.")}</p>
                  {me && <Link
                    to="/business/dashboard"
                    className="inline-block mt-4 bg-[#171717] text-white px-5 py-3 text-[12px] font-bold font-mono uppercase tracking-wider"
                  >
                    {t("claim.dashboard")}
                  </Link>}
                </div>
              ) : mine?.status === "Pending" ? (
                <div className="text-center py-4">
                  <h3 className="text-[17px] font-black">{t("claim.review")}</h3>
                  <p className="text-[14px] text-[#6d6d6a] mt-1.5 leading-[1.55]">{t("claim.reviewBlurb")}</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3.5">
                  <p className="text-[14px] text-[#6d6d6a] leading-[1.55]">{t("claim.intro")}</p>
                  <div>
                    <label className={labelCls}>{t("claim.name")}</label>
                    <input
                      className={inputCls}
                      value={form.claimant_name}
                      onChange={(e) => setForm({ ...form, claimant_name: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className={labelCls}>{t("claim.email")}</label>
                      <input
                        type="email"
                        className={inputCls}
                        value={form.claimant_email}
                        onChange={(e) => setForm({ ...form, claimant_email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>{t("claim.phone")}</label>
                      <input
                        className={inputCls}
                        value={form.claimant_phone}
                        onChange={(e) => setForm({ ...form, claimant_phone: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className={labelCls}>{t("claim.role")}</label>
                      <select
                        className={inputCls}
                        value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                      >
                        {ROLES.map((r) => (
                          <option key={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>{t("claim.link")}</label>
                      <input
                        className={inputCls}
                        value={form.website}
                        onChange={(e) => setForm({ ...form, website: e.target.value })}
                        placeholder="https://"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Promo code (optional)</label>
                    <input
                      className={inputCls}
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
                      placeholder="Enter a seller promo code"
                    />
                    <p className="text-[11px] text-[#777773] mt-1">If someone referred you, enter their code so we can credit the right seller.</p>
                  </div>
                  <div>
                    <label className={labelCls}>{t("claim.note")}</label>
                    <textarea
                      rows={3}
                      className={inputCls}
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                  </div>

                  {err && <div className="text-[13px] font-bold text-[#ef3340]">{err}</div>}

                  <button
                    disabled={sending}
                    className="w-full bg-[#171717] text-white px-5 py-3.5 text-[12px] font-bold font-mono uppercase tracking-wider disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> {t("claim.sending")}
                      </>
                    ) : (
                      t("claim.submit")
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}